// ============================================================================
// CHANGO V5.0 — <AppShell> — D3 authenticated app chrome
// ============================================================================
// Shared layout for the user-center SPA (/app/*). Provides:
//   - left sidebar with role-filtered nav links
//   - top bar with workspace/role badge + logout
//   - <Outlet /> for nested routes
//
// Role-aware rendering:
//   canAccessPath() from utils/rolePermissions is the single source of truth
//   for link visibility. Boss sees everything; finance sees everything except
//   workspace/settings (unless they're also granted); sales never sees
//   anything that prefixes "/app/ops" or "/workspace/settings" etc.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth.js';
import { canAccessPath } from '../utils/rolePermissions.js';
import { hasWorkspaceLayer } from '../utils/layerPermissions.js';
import CommandPalette from './CommandPalette.jsx';
import ReconsentGate from './ReconsentGate.jsx';
import LayersChangeToast from './LayersChangeToast.jsx';

function SidebarLink({ to, label, badge, description, role }) {
  if (!canAccessPath(role, to)) return null;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'group flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-chango-100 text-chango-900 font-semibold'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
        ].join(' ')
      }
      title={description}
    >
      <span>{label}</span>
      {badge && (
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function SidebarGroup({ title, stage, summary, items, role, collapsed, onToggle }) {
  const visible = items.filter((it) => canAccessPath(role, it.to));
  if (visible.length === 0) return null;
  return (
    <section className="px-3 pb-3">
      <button
        type="button"
        onClick={onToggle}
        className="mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
        aria-expanded={!collapsed}
      >
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-100 text-[11px] text-slate-700">
          {stage}
        </span>
        <span className="min-w-0 flex-1 truncate">{title}</span>
        <span className="text-[10px] text-slate-400" aria-hidden="true">
          {collapsed ? '展开' : '收起'}
        </span>
      </button>
      {collapsed ? (
        <div className="mx-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
          <div className="text-xs font-medium text-slate-800">{title}</div>
          <div className="mt-1 text-[11px] leading-4 text-slate-500">{summary || visible.map((it) => it.label).join('、')}</div>
        </div>
      ) : (
        <nav className="space-y-1">
          {visible.map((it) => (
            <SidebarLink key={it.to} role={role} {...it} />
          ))}
        </nav>
      )}
    </section>
  );
}

function QuoteChainBlock({ collapsed, onToggle, hasFactoryLayer, hasTradeLayer, hasChannelLayer, hasEntityLayer }) {
  const layers = [
    { key: 'A', label: '工厂', enabled: hasFactoryLayer },
    { key: 'B', label: '外贸', enabled: hasTradeLayer },
    { key: 'C', label: '电商', enabled: hasChannelLayer },
    { key: 'E', label: '运营主体层', enabled: hasEntityLayer }
  ];
  const enabledLayers = layers.filter((layer) => layer.enabled);
  const enabledSummary = enabledLayers.length > 0
    ? enabledLayers.map((layer) => `${layer.key}${layer.label}`).join('、')
    : '基础报价';

  return (
    <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left font-semibold text-slate-700 transition-colors hover:bg-slate-50"
        aria-expanded={!collapsed}
      >
        <span>报价链路</span>
        <span className="text-[10px] font-semibold text-slate-400" aria-hidden="true">
          {collapsed ? '展开' : '收起'}
        </span>
      </button>
      {collapsed ? (
        <div className="mt-1 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] leading-4 text-slate-600">
          当前层级：{enabledSummary}
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <div className="leading-5 text-slate-600">业务链路：客户 → 工厂 → 外贸 → 电商 → 运营主体层</div>
          <div className="leading-5 text-slate-600">回款路径：平台 → 海外主体 → 国内分红/服务费</div>
          <div className="flex flex-wrap gap-1.5">
            {layers.map((layer) => (
              <span
                key={layer.key}
                className={[
                  'inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold',
                  layer.enabled
                    ? 'border-chango-200 bg-chango-50 text-chango-800'
                    : 'border-slate-200 bg-white text-slate-400'
                ].join(' ')}
              >
                {layer.key} · {layer.label}{layer.enabled ? '已启用' : '未启用'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarToggle({ collapsed, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950',
        collapsed ? 'h-8 w-full px-2' : 'h-8 px-3'
      ].join(' ')}
      aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
      aria-expanded={!collapsed}
      title={collapsed ? '展开侧边栏' : '收起侧边栏'}
    >
      {collapsed ? '展开侧栏' : '收起侧边栏'}
    </button>
  );
}

function AccountMenu({ user, role, roleLabel, onLogout }) {
  const [open, setOpen] = useState(false);
  const name = user?.display_name || user?.contact_name || user?.email || '账号';
  const isBoss = role === 'boss';
  const isBossOrFinance = role === 'boss' || role === 'finance';
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
      >
        <span className="max-w-[220px] truncate">{name}</span>
        <span className="rounded bg-chango-50 px-2 py-0.5 text-xs font-medium text-chango-800">
          {roleLabel}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-56 rounded-md border border-slate-200 bg-white py-2 text-sm shadow-lg">
          {isBossOrFinance && (
            <Link className="block px-4 py-2 text-slate-800 hover:bg-slate-50" to="/app/billing/plans" onClick={() => setOpen(false)}>
              套餐与会员
            </Link>
          )}
          {isBoss && (
            <>
              <Link className="block px-4 py-2 text-slate-800 hover:bg-slate-50" to="/app/settings" onClick={() => setOpen(false)}>
                工作区设置
              </Link>
              <Link className="block px-4 py-2 text-slate-800 hover:bg-slate-50" to="/workspace/settings" onClick={() => setOpen(false)}>
                账号与权限
              </Link>
            </>
          )}
          <button
            type="button"
            className="mt-1 block w-full border-t border-slate-100 px-4 py-2 text-left text-slate-800 hover:bg-slate-50"
            onClick={onLogout}
          >
            登出
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role;
  const displayName = user?.display_name || user?.contact_name || user?.email || '未命名用户';
  const hasFactoryLayer = hasWorkspaceLayer(workspace, 'A');
  const hasTradeLayer = hasWorkspaceLayer(workspace, 'B');
  const hasChannelLayer = hasWorkspaceLayer(workspace, 'C');
  const hasEntityLayer = hasWorkspaceLayer(workspace, 'E');
  const isQuoteWorkspaceRoute = /\/quotes\/(?:new|\d+\/edit)\/?$/.test(location.pathname);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [quoteChainCollapsed, setQuoteChainCollapsed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => isQuoteWorkspaceRoute);
  const effectiveSidebarCollapsed = isQuoteWorkspaceRoute || sidebarCollapsed;
  const autoCollapsedQuotePathRef = useRef(null);
  const roleLabel = {
    boss: '老板',
    finance: '财务',
    sales: '销售',
    support: '客服',
    ops: '运营',
    admin: '管理员',
    editor: '编辑'
  }[role] || role || '—';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (!isQuoteWorkspaceRoute) return;
    if (autoCollapsedQuotePathRef.current === location.pathname) return;
    setSidebarCollapsed(true);
    autoCollapsedQuotePathRef.current = location.pathname;
  }, [isQuoteWorkspaceRoute, location.pathname]);

  const navGroups = useMemo(() => {
    const bossOrFinance = role === 'boss' || role === 'finance';
    return [
      {
        title: '工作台',
        stage: '0',
        summary: '总览',
        items: [
          { to: '/app/dashboard', label: '总览', description: '工作区指标与待办' }
        ]
      },
      {
        title: '客户',
        stage: '1',
        summary: bossOrFinance ? '客户档案、客户分组' : '客户档案',
        items: [
          { to: '/app/customers', label: '客户档案', description: '客户分层、联系人、报价历史' },
          ...(bossOrFinance ? [{ to: '/app/customers/groups', label: '客户分组', description: '客户分类口径' }] : [])
        ]
      },
      {
        title: '工厂',
        stage: '2',
        summary: hasFactoryLayer && bossOrFinance ? '物料、工序、产品目录' : '产品目录',
        items: hasFactoryLayer && bossOrFinance
          ? [
              { to: '/app/products', label: '产品目录', description: '产品、默认价格和成本' },
              { to: '/app/factory/material-categories', label: '物料大类', badge: 'A', description: '物料分类管理' },
              { to: '/app/factory/bom-items', label: '物料库', badge: 'A', description: '原料与零件库' },
              { to: '/app/factory/processes', label: '工序库', badge: 'A', description: '生产工序管理' }
            ]
          : [
              { to: '/app/products', label: '产品目录', description: '报价行项目来源' }
            ]
      },
      {
        title: '外贸',
        stage: '3',
        summary: bossOrFinance ? '报价单、新建报价链' : '报价单',
        items: [
          { to: '/app/quotes', label: '报价单', badge: hasTradeLayer ? 'B' : null, description: '外贸报价、审批和分享' },
          ...(bossOrFinance ? [{ to: '/app/quotes/new', label: '新建报价链', description: '从客户、产品、成本进入报价链' }] : [])
        ]
      },
      {
        title: '电商',
        stage: '4',
        summary: '渠道测算',
        items: hasChannelLayer && bossOrFinance
          ? [{ to: '/app/crossborder', label: '渠道测算', badge: 'C', description: '平台佣金、广告、FBA、退货和渠道净利' }]
          : []
      },
      {
        title: '运营主体层',
        stage: '5',
        summary: '运营主体利润',
        items: hasEntityLayer && bossOrFinance
          ? [{ to: '/app/entities', label: '运营主体利润', badge: 'E', description: '海外主体分配、海外所得税和集团净利' }]
          : []
      }
    ];
  }, [hasChannelLayer, hasEntityLayer, hasFactoryLayer, hasTradeLayer, role]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex min-h-screen">
        {/* ---- Sidebar ------------------------------------------------ */}
        <aside
          className={[
            'flex shrink-0 flex-col border-r border-slate-200 bg-white transition-[width] duration-200 ease-out',
            effectiveSidebarCollapsed ? 'w-20' : 'w-64'
          ].join(' ')}
          aria-label="应用侧边栏"
        >
          <div className={effectiveSidebarCollapsed ? 'border-b border-slate-100 px-2 py-4' : 'border-b border-slate-100 px-5 py-5'}>
            {effectiveSidebarCollapsed ? (
              <div className="space-y-3">
                <Link
                  to="/app/dashboard"
                  className="mx-auto flex h-10 w-10 items-center justify-center rounded-md bg-chango-50 text-xs font-semibold text-chango-800"
                  title={`长戈 · CHANGO / ${workspace?.name || '未关联工作区'}`}
                  aria-label="长戈 · CHANGO"
                >
                  长戈
                </Link>
                {!isQuoteWorkspaceRoute && (
                  <SidebarToggle
                    collapsed={effectiveSidebarCollapsed}
                    onToggle={() => setSidebarCollapsed((current) => !current)}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <Link to="/app/dashboard" className="min-w-0 flex-1">
                    <div className="text-sm font-semibold tracking-wide text-chango-700">
                      长戈 · CHANGO
                    </div>
                    <div className="mt-1 truncate text-xs text-slate-500">
                      {workspace?.name || '(未关联工作区)'}
                    </div>
                  </Link>
                  <SidebarToggle
                    collapsed={effectiveSidebarCollapsed}
                    onToggle={() => setSidebarCollapsed((current) => !current)}
                  />
                </div>
              </div>
            )}
          </div>
          {!effectiveSidebarCollapsed && (
            <>
              <div className="flex-1 overflow-y-auto py-4">
                {navGroups.map((group) => (
                  <SidebarGroup
                    key={group.title}
                    role={role}
                    collapsed={Boolean(collapsedGroups[group.title])}
                    onToggle={() => setCollapsedGroups((current) => ({
                      ...current,
                      [group.title]: !current[group.title]
                    }))}
                    {...group}
                  />
                ))}
              </div>

              <QuoteChainBlock
                collapsed={quoteChainCollapsed}
                onToggle={() => setQuoteChainCollapsed((current) => !current)}
                hasFactoryLayer={hasFactoryLayer}
                hasTradeLayer={hasTradeLayer}
                hasChannelLayer={hasChannelLayer}
                hasEntityLayer={hasEntityLayer}
              />
            </>
          )}
        </aside>

        {/* ---- Main column -------------------------------------------- */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between px-6 py-3">
              <div className="text-sm text-slate-600">
                {workspace?.company_name || '出口决策云'}
              </div>
              <div className="flex items-center gap-3 text-sm">
                {/* Search hint — the palette itself binds the real hotkey. */}
                <button
                  type="button"
                  onClick={() => {
                    // Synthesize the same hotkey the palette listens for.
                    const evt = new KeyboardEvent('keydown', {
                      key: 'k',
                      metaKey: true,
                      ctrlKey: true,
                      bubbles: true,
                    });
                    window.dispatchEvent(evt);
                  }}
                  className="hidden md:inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                  title="全局搜索（⌘K / Ctrl+K）"
                >
                  <span aria-hidden="true">🔎</span>
                  <span>搜索</span>
                  <kbd className="rounded bg-white border border-slate-200 px-1 font-mono text-[10px]">⌘K</kbd>
                </button>
                <AccountMenu user={{ ...user, display_name: displayName }} role={role} roleLabel={roleLabel} onLogout={handleLogout} />
              </div>
            </div>
          </header>

          {/* Route outlet — the actual page renders here. */}
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
      {/* Global ⌘K / Ctrl+K search palette. Binds its own hotkey. */}
      <CommandPalette />
      {/* §11.10 — blocks writes after TOS/Privacy version bump until re-consent. */}
      <ReconsentGate />
      {/* §9.5.1 — toast 提示 ops 后台改了套餐, 点"立即刷新"或自动 3 秒后刷新 */}
      <LayersChangeToast />
    </div>
  );
}
